/**
 * Axios HTTP client cho backend `/v1/*`.
 *
 * - Đọc baseURL từ env `VITE_API_BASE_URL`.
 * - Auto attach `Authorization: Bearer <JWT>` từ in-memory token store.
 * - Bắt 401 → clear token + emit event để FE redirect /login.
 *
 * Token KHÔNG lưu `localStorage` Phase 1 (an toàn hơn — không leak qua XSS).
 * Khi reload tab: user phải login lại. Phase 2+ có thể thêm refresh token.
 */
import axios, { AxiosError, type AxiosInstance } from "axios";

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/v1";

const TOKEN_STORAGE_KEY = "cafechuquan_token";

let accessToken: string | null = (() => {
  // Phase 1 đơn giản: lưu localStorage để reload tab không phải login lại.
  // Trade-off security: XSS có thể đọc → chấp nhận cho dev/soft test. Phase 2
  // thêm refresh token + token rotation.
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
})();
type UnauthorizedListener = () => void;
const unauthorizedListeners: UnauthorizedListener[] = [];

export function setAccessToken(token: string | null): void {
  accessToken = token;
  try {
    if (token === null) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    } else {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
  } catch {
    // SSR / private mode — ignore.
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.push(listener);
  return () => {
    const idx = unauthorizedListeners.indexOf(listener);
    if (idx >= 0) unauthorizedListeners.splice(idx, 1);
  };
}

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10_000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      accessToken = null;
      for (const listener of unauthorizedListeners) {
        try {
          listener();
        } catch {
          // ignore listener error
        }
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Trích error code từ response envelope:
 * `{ "error": { "code": "...", "message": "...", ... } }`
 */
export function extractErrorCode(err: unknown): string | null {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: { code?: string } } | undefined;
    return data?.error?.code ?? null;
  }
  return null;
}

export function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { error?: { message?: string } }
      | undefined;
    if (data?.error?.message) return data.error.message;
    if (err.message) return err.message;
  }
  return "Lỗi không xác định";
}
